import { useEffect } from 'react'
import { useAuth } from '@structural-climbing/auth'
import type { Discipline } from '@structural-climbing/validation'
import { useStandings } from '../standings/useStandings'
import { readSessionStart, rememberSessionStart, type SessionStart } from './sessionStart'

/**
 * Where you stand on one board (bouldering or lead), and how far this visit
 * has moved you on it.
 *
 * The board is the all-time, everyone slice: the one the rank card and the
 * Standings sheet open on, so it shares their cached request. Pass a null
 * discipline to ask for nothing, which a single-wall gym does for the second.
 */
export function useBoardProgress({
  gymId,
  discipline,
  enabled,
  isCheckedIn,
  visitStart,
}: {
  gymId: string
  discipline: Discipline | null
  enabled: boolean
  isCheckedIn: boolean
  visitStart: string | null
}) {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { data } = useStandings(
    enabled && discipline ? gymId : null,
    apiUrl,
    getAccessTokenSilently,
    'all',
    'all',
    discipline ?? 'boulder',
  )
  const myEntry = enabled && discipline ? (data?.myEntry ?? null) : null
  const key = `${gymId}:${discipline}`

  // The first rank seen during a visit is where it started.
  useEffect(() => {
    if (isCheckedIn && visitStart && myEntry && discipline)
      rememberSessionStart(key, {
        checkedInAt: visitStart,
        rank: myEntry.rank,
        score: myEntry.totalScore,
      })
  }, [isCheckedIn, visitStart, myEntry, discipline, key])

  const start: SessionStart | null =
    visitStart && discipline ? readSessionStart(key, visitStart) : null
  const pointsGained = start && myEntry ? Math.round(myEntry.totalScore - start.score) : null
  // Never topped on this wall: the board answered, has no row for you, and not
  // because the profile is private (a private profile gets no row either).
  const noTopsYet = Boolean(enabled && discipline && data && !data.myEntry && !data.myIsPrivate)
  return { myEntry, start, pointsGained, noTopsYet }
}
